import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { adminClient, type AdminRunDetailResponse } from '@/api/adminClient';
import { RunEventLog } from '@/components/runs/RunEventLog';
import { RunFeedbackSummary } from '@/components/runs/RunFeedbackSummary';
import { RunPhaseTimeline } from '@/components/runs/RunPhaseTimeline';
import { RunTelemetryPanel } from '@/components/runs/RunTelemetryPanel';
import { RunTraceWaterfall } from '@/components/runs/RunTraceWaterfall';
import { RunVerdictCard } from '@/components/runs/RunVerdictCard';
import { WaitBreakdown } from '@/components/runs/WaitBreakdown';
import { AdminBreadcrumbs } from '@/components/shell/AdminBreadcrumbs';
import { AdminMetaItem } from '@/components/shell/AdminMetaItem';
import { PAGE_CONTAINER_CLASS, PAGE_TITLE_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminPageLoadError } from '@/components/shell/AdminPageLoadError';
import { AdminSection } from '@/components/shell/AdminSection';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { orgBasePath, orgMoneyPath, orgSectionPath } from '@/lib/orgNav';
import { humanizeKey } from '@/lib/runTelemetry';
import { runVerdict } from '@/lib/runVerdict';
import { useOrgScope } from '@/pages/org/OrgLayout';

const LIVE_REFRESH_MS = 3_000;
const TERMINAL_RUN_STATUSES = new Set(['preview_ready', 'failed', 'blocked']);
const RUN_TABS = ['timeline', 'model', 'prompt', 'events'] as const;
type RunTab = (typeof RUN_TABS)[number];

function isRunTab(value: string | null): value is RunTab {
  return RUN_TABS.includes(value as RunTab);
}

export function RunDetailPage() {
  const { runId = '' } = useParams();
  const { orgId } = useOrgScope();
  const [detail, setDetail] = useState<AdminRunDetailResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const tab: RunTab = isRunTab(requestedTab) ? requestedTab : 'timeline';
  const selectTab = (value: string) => {
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        if (value === 'timeline') next.delete('tab');
        else next.set('tab', value);
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const next = await adminClient.getRun(orgId, runId);
        if (cancelled) return;
        setDetail(next);
        setError(undefined);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load run');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, runId, reloadKey]);

  const inFlight = Boolean(detail && (detail.trace?.inProgress ?? !TERMINAL_RUN_STATUSES.has(detail.run.status)));
  useEffect(() => {
    if (!inFlight) return undefined;
    let cancelled = false;
    const timer = setInterval(() => {
      void adminClient
        .getRun(orgId, runId)
        .then((next) => {
          if (!cancelled) setDetail(next);
        })
        .catch(() => {
          // The next tick retries; the page keeps what it last loaded.
        });
    }, LIVE_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [inFlight, orgId, runId]);

  const orgPath = orgBasePath(orgId);

  if (loading) {
    return (
      <div className={PAGE_CONTAINER_CLASS}>
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading run telemetry
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <AdminPageLoadError
        message={error}
        onRetry={() => setReloadKey((key) => key + 1)}
        backTo={orgMoneyPath(orgId, 'spend')}
        backLabel="Back to spend"
      />
    );
  }

  if (!detail) {
    return (
      <div className={PAGE_CONTAINER_CLASS}>
        <div className="space-y-3">
          <p className="text-sm text-destructive">Run not found</p>
          <Link to={orgMoneyPath(orgId, 'spend')} className="text-sm text-primary hover:underline">
            Back to spend
          </Link>
        </div>
      </div>
    );
  }

  const { run, session, project, telemetry } = detail;
  const projectLabel = project?.displayName || project?.title || run.projectId;
  const sessionPath = `${orgPath}/sessions/${encodeURIComponent(run.sessionId)}`;
  const kind = run.qualityRepair ? 'Quality repair' : run.mode && run.mode !== 'normal' ? humanizeKey(run.mode) : 'Build';

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminBreadcrumbs
        items={[
          { label: 'Projects', to: orgSectionPath(orgId, 'projects') },
          { label: projectLabel, to: `${orgPath}/projects/${encodeURIComponent(run.projectId)}` },
          { label: session?.title || 'Session', to: sessionPath },
          { label: `Run ${run.runId.slice(0, 8)}` },
        ]}
      />

      <header className="mb-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <span className="inline-block rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
              {kind}
              {run.attempt && run.attempt > 1 ? ` · attempt ${run.attempt}` : ''}
            </span>
            <h1 className={PAGE_TITLE_CLASS}>{session?.title || 'Untitled session'}</h1>
            {run.promptSnippet ? (
              <p className="max-w-3xl text-sm leading-6 text-muted-foreground">{run.promptSnippet}</p>
            ) : null}
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to={sessionPath}>All runs in session</Link>
          </Button>
        </div>
        <RunVerdictCard verdict={runVerdict(detail)} />
        <dl className="grid gap-4 rounded-xl border border-border px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <AdminMetaItem label="Started by">{run.createdByName || run.createdByUserId || '—'}</AdminMetaItem>
          <AdminMetaItem label="Package">
            <span className="font-mono text-xs">{project?.packageName || '—'}</span>
          </AdminMetaItem>
          <AdminMetaItem label="Run id">
            <span className="font-mono text-xs" title={run.runId}>
              {run.runId}
            </span>
          </AdminMetaItem>
          <AdminMetaItem label="Changes">
            {run.stats?.filesChanged !== undefined
              ? `${run.stats.filesChanged} files · +${run.stats.additions ?? 0} / −${run.stats.deletions ?? 0}`
              : '—'}
          </AdminMetaItem>
          <div className="min-w-0 sm:col-span-2 lg:col-span-4">
            <dt className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Feedback</dt>
            <dd className="mt-1 text-sm text-foreground">
              <RunFeedbackSummary feedback={detail.feedback} />
            </dd>
          </div>
        </dl>
      </header>

      <Tabs value={tab} onValueChange={selectTab}>
        <TabsList className="h-auto flex-wrap justify-start">
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="model">Model &amp; tokens</TabsTrigger>
          <TabsTrigger value="prompt">Prompt</TabsTrigger>
          <TabsTrigger value="events">Events ({detail.events.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="space-y-6">
          <AdminSection
            title="Execution trace"
            description={
              inFlight
                ? 'Every step this run has recorded so far, on one clock. It refreshes every 3 s while the run is going. Click a step for its details.'
                : 'Every step this run recorded, on one clock: getting a workspace, the agent turn, and building the preview. Click a step for its details.'
            }
          >
            <RunTraceWaterfall trace={detail.trace} microvm={detail.microvm} />
          </AdminSection>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <AdminSection
              title="Where the wait went"
              description="The time from the Build click (or follow-up prompt) to the first file the agent saved, split by phase."
            >
              <WaitBreakdown telemetry={telemetry} />
            </AdminSection>
            <AdminSection
              title="Key phases"
              description="Times are counted from the Build click. Long gaps between phases are highlighted."
            >
              <RunPhaseTimeline markers={telemetry.markers} />
            </AdminSection>
          </div>
        </TabsContent>

        <TabsContent value="model">
          <AdminSection
            title="Model and tokens"
            description="Which model ran, how fast it answered, how much context it was given and how much it read. Anything the run did not report says so."
          >
            <RunTelemetryPanel telemetry={telemetry} />
          </AdminSection>
        </TabsContent>

        <TabsContent value="prompt">
          <AdminSection title="Prompt" description="What the person asked for in this run.">
            {run.prompt ? (
              <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap break-words font-sans text-sm leading-6 text-muted-foreground">
                {run.prompt}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground">No prompt was stored for this run.</p>
            )}
          </AdminSection>
        </TabsContent>

        <TabsContent value="events">
          <AdminSection
            title="Events"
            description="Every event this run stored, in order. Expand a row to see its raw payload."
          >
            <RunEventLog
              events={detail.events}
              truncated={detail.eventsTruncated}
              heartbeatCount={telemetry.heartbeatCount}
              streamLineCount={detail.streamLineCount}
            />
          </AdminSection>
        </TabsContent>
      </Tabs>
    </div>
  );
}
